import { AxiosError } from 'axios'

interface ValidationErrorItem {
  loc?: (string | number)[]
  msg?: string
  type?: string
}

interface ErrorResponseData {
  /**
   * - 400/401/403/404/500 (`ErrorResponse`): `errors: string[]`
   * - 422 (`HTTPValidationError`): `detail: ValidationError[]`
   * - Some legacy endpoints: `detail: string`
   */
  detail?: string | ValidationErrorItem[]
  errors?: string[]
}

export type CustomAxiosError = AxiosError<ErrorResponseData>

const ERROR_MESSAGE_FALLBACK = 'An unexpected error occurred'

export const isAxiosError = (error: unknown): error is CustomAxiosError => {
  return error instanceof AxiosError
}

export const extractServerMessage = (error: unknown): string | null => {
  if (!(error instanceof AxiosError)) return null
  const data = error.response?.data as unknown
  if (data == null) return null

  if (typeof data === 'string' && data.trim()) return data
  if (typeof data !== 'object') return null

  const record = data as Record<string, unknown>

  if (typeof record.detail === 'string' && record.detail.trim()) {
    return record.detail
  }

  if (Array.isArray(record.detail)) {
    const parts = (record.detail as ValidationErrorItem[])
      .map((v) => {
        const loc =
          Array.isArray(v.loc) && v.loc.length > 1 ? `${v.loc.slice(1).join('.')}: ` : ''
        return v.msg ? `${loc}${v.msg}` : null
      })
      .filter((s): s is string => !!s)
    if (parts.length) return parts.join(' · ')
  }

  if (Array.isArray(record.errors) && record.errors.length) {
    const parts = (record.errors as unknown[]).filter(
      (s): s is string => typeof s === 'string' && s.trim().length > 0
    )
    if (parts.length) return parts.join(' · ')
  }

  // Last-resort: surface any plain-string field we can find (common DRF shapes: `message`, `error`).
  for (const key of ['message', 'error']) {
    const v = record[key]
    if (typeof v === 'string' && v.trim()) return v
  }

  // Field-level errors (DRF serializer): { field: ["msg", ...] }
  const fieldErrors: string[] = []
  for (const [key, v] of Object.entries(record)) {
    if (key === 'detail' || key === 'errors' || key === 'message' || key === 'error') continue
    if (Array.isArray(v)) {
      for (const m of v) {
        if (typeof m === 'string' && m.trim()) fieldErrors.push(`${key}: ${m}`)
      }
    } else if (typeof v === 'string' && v.trim()) {
      fieldErrors.push(`${key}: ${v}`)
    }
  }
  if (fieldErrors.length) return fieldErrors.join(' · ')

  return null
}

export const getErrorMessage = (error: unknown): string => {
  const serverMessage = extractServerMessage(error)
  if (serverMessage) return serverMessage

  if (error instanceof AxiosError) {
    const status = error.response?.status
    const statusText = error.response?.statusText
    if (status) {
      // Log the raw response body so we can still diagnose unexpected shapes from DevTools.
      // eslint-disable-next-line no-console
      console.error('[API error]', status, statusText, error.response?.data)
      return statusText ? `${status} ${statusText}` : `HTTP ${status}`
    }
    return error.message || ERROR_MESSAGE_FALLBACK
  }

  if (error instanceof Error) {
    return error.message
  }

  return ERROR_MESSAGE_FALLBACK
}
